import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, ArrowLeft } from 'lucide-react';
import blogPosts, { getCategories, getFeaturedPost } from '../data/blogPosts';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tất Cả');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = getCategories();
  const featuredPost = getFeaturedPost();

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'Tất Cả' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Non-featured posts for the grid
  const gridPosts = filteredPosts.filter(post => post.id !== featuredPost.id);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        .network-bg {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        .hero-glow {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
        }

        .card-hover {
          transition: all 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#10b981] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Trang Chủ</span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-[#10b981] font-semibold border-b-2 border-[#10b981] pb-1">Blog</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 hero-glow network-bg">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 mb-6">
            <span className="text-[#10b981] font-semibold text-sm tracking-wider uppercase">Kiến Thức Vay Nhà</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Blog Của <span className="text-[#10b981]">Chúng Tôi</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto">
            Hướng dẫn chi tiết, mẹo hay, và cập nhật mới nhất về vay mua nhà tại Mỹ dành cho người Việt.
          </p>
        </div>
      </section>

      {/* Blog Content Section */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Blog Header Card */}
          <div className="bg-[#141b2d] rounded-3xl p-8 mb-8 border border-gray-800">
            <h2 className="text-3xl font-bold text-center mb-8">Blog</h2>

            {/* Search and Filters */}
            <div className="flex flex-wrap items-center justify-center gap-4 border-t border-gray-700 pt-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm kiếm..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-[#0a0f1c] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#10b981] w-48"
                />
              </div>

              {/* Category Filters */}
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === category
                      ? 'text-[#10b981] bg-[#10b981]/10'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Featured Post */}
          {selectedCategory === 'Tất Cả' && !searchQuery && (
            <Link to={`/blog/${featuredPost.slug}`} className="block mb-8">
              <div className="bg-white rounded-2xl overflow-hidden card-hover flex flex-col md:flex-row">
                <div className="md:w-1/2">
                  <img
                    src={featuredPost.featuredImage}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 hover:text-[#10b981] transition-colors">
                    {featuredPost.title}
                  </h3>
                  <span className="text-gray-500 text-sm uppercase tracking-wider mb-3">
                    {featuredPost.category}
                  </span>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-gray-500 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime} phút đọc
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(searchQuery || selectedCategory !== 'Tất Cả' ? filteredPosts : gridPosts).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="block">
                <div className="bg-[#141b2d] rounded-2xl overflow-hidden card-hover border border-gray-800 h-full">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-gray-500 text-sm mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime} phút đọc
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 hover:text-[#10b981] transition-colors">
                      {post.title}
                    </h3>
                    <span className="text-[#10b981] text-sm uppercase tracking-wider">
                      {post.category}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* No Results */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">Không tìm thấy bài viết nào.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2026 Nhamy.us. Bảo lưu mọi quyền.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
